import { units } from "@/lib/format"
import type { WarehouseStock, WarehouseStockRow } from "../types"
import Section from "./Section"

function sharePct(stock: number, total: number): string {
  if (total <= 0) return "0%"
  return `${Math.round((stock / total) * 100)}%`
}

function WarehouseRow({ row, total }: { row: WarehouseStockRow; total: number }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-foreground">
      <div className="min-w-0 flex-1 truncate">
        <span>{row.name}</span>
        {row.code && <span> ({row.code})</span>}
        {row.channel_name && <span className="ml-1 text-xs">· {row.channel_name}</span>}
      </div>
      <span className="tabular-nums">{units(row.stock)}</span>
      <span className="w-10 text-right text-xs tabular-nums">{sharePct(row.stock, total)}</span>
    </div>
  )
}

function GroupLabel({ label }: { label: string }) {
  return <div className="px-2 text-xs font-medium text-foreground">{label}</div>
}

// Same list as the old Inventory panel: warehouses holding the product, with
// code, channel, units and share. This market's warehouses first, other
// markets' below.
export default function StockByWarehouse({ stock, marketName }: { stock: WarehouseStock; marketName: string }) {
  const warehouses = stock.warehouses.filter((w) => w.stock > 0)
  const inMarket = warehouses.filter((w) => w.in_scope)
  const others = warehouses.filter((w) => !w.in_scope)

  return (
    <Section title="Stock by warehouse" aside={`Total on hand: ${units(stock.total)}`}>
      {warehouses.length === 0 ? (
        <p className="text-sm text-foreground">No stock recorded for this product.</p>
      ) : (
        <>
          <div className="flex flex-col gap-1">
            <GroupLabel label={`In ${marketName}`} />
            {inMarket.length > 0 ? (
              <div className="rounded-md bg-primary/5">
                {inMarket.map((w) => (
                  <WarehouseRow key={w.external_id} row={w} total={stock.total} />
                ))}
              </div>
            ) : (
              <p className="px-2 text-sm text-foreground">No stock in {marketName}</p>
            )}
          </div>
          {others.length > 0 && (
            <div className="flex flex-col gap-1">
              <GroupLabel label="Other warehouses" />
              {others.map((w) => (
                <WarehouseRow key={w.external_id} row={w} total={stock.total} />
              ))}
            </div>
          )}
        </>
      )}
    </Section>
  )
}
